// Static methods container
import { UiConstants } from '../../ui/constants/ui-constants'

export class Utils {
    static workingDayInSeconds = 28800 // 8 hours
    static hourInSeconds = 3600
    static minuteInSeconds = 60
    private static readonly prefix = 'Utils: '

    /**
     * An optional function passed into the NgForOf directive that defines how to track changes for items in an iterable.
     * The function takes the iteration index and item ID. When supplied, Angular tracks changes by the return value of the function.
     * Description taken from: https://angular.io/api/core/TrackByFunction
     */
    static trackByIndex(index: number): number {
        return index
    }

    // Example output: 15 Days, 4.50 Hours
    static secondsAsWholeWorkingDaysAndHours(seconds: number): string {
        if (seconds != null) {
            const days = Math.floor(seconds / Utils.workingDayInSeconds)
            const remainingSeconds = seconds - days * Utils.workingDayInSeconds
            const hours = Utils.secondsAsHours(remainingSeconds)

            return `${days} ${days === 1 ? 'Day' : 'Days'}, ${hours} Hours`
        }
        throw new Error(`${Utils.prefix}No seconds received for calculation`)
    }

    static secondsAsHours(seconds: number): string {
        return Utils.secondsAs(seconds, Utils.hourInSeconds)
    }

    static secondsAsWorkingDay(seconds: number): string {
        return Utils.secondsAs(seconds, Utils.workingDayInSeconds)
    }

    static secondsAs(seconds: number, rep: number): string {
        if (seconds !== null && rep) {
            return (seconds / rep).toFixed(2)
        }

        return ''
    }

    static isValidJSON(json: string): boolean {
        let valid = true
        try {
            JSON.parse(json)
        } catch (err) {
            valid = false
        }

        return valid
    }

    static secondsToTimeString(seconds: number): string {
        if (seconds !== null && seconds !== undefined) {
            const days = Math.floor(seconds / Utils.workingDayInSeconds)
            let secondsLeft = seconds - days * Utils.workingDayInSeconds

            const hours = Math.floor(secondsLeft / Utils.hourInSeconds)
            secondsLeft -= hours * Utils.hourInSeconds

            const minutes = Math.floor(secondsLeft / Utils.minuteInSeconds)
            secondsLeft -= minutes * Utils.minuteInSeconds

            let timeString = ''

            if (days) {
                timeString += days + TimeUnit[TimeUnit.d]
            }

            if (hours) {
                timeString += timeString ? ` ${hours}${TimeUnit[TimeUnit.h]}` : hours + TimeUnit[TimeUnit.h]
            }

            if (minutes) {
                timeString += timeString ? ` ${minutes}${TimeUnit[TimeUnit.m]}` : minutes + TimeUnit[TimeUnit.m]
            }

            if (secondsLeft) {
                timeString += timeString ? ` ${secondsLeft}${TimeUnit[TimeUnit.s]}` : secondsLeft + TimeUnit[TimeUnit.s]
            }

            return timeString
        }

        throw new Error(`${Utils.prefix}No seconds received to perform conversion`)
    }

    // Method to format string into seconds
    // Example: '1d 2h 15m' will return 28935
    // 'd' is a day meaning 8 working hours
    // 'h' is 60 minutes
    // 'm' is 1 minute
    static timeUnitStringToSeconds(time: string): number {
        let amountOfDays: TimeUnitExtractResult
        let amountOfHours: TimeUnitExtractResult
        let amountOfMinutes: TimeUnitExtractResult
        let amountOfSeconds: TimeUnitExtractResult

        if (time) {
            // Convert ex. 6h 30m 15s into 6h30m15s
            const withoutSpaces = time.replace(/\s/g, '')

            // Extract days
            amountOfDays = Utils.substractTimeUnit(withoutSpaces, TimeUnit.d)

            // Extract hours
            amountOfHours = Utils.substractTimeUnit(amountOfDays.remaining, TimeUnit.h)

            // Extract minutes
            amountOfMinutes = Utils.substractTimeUnit(amountOfHours.remaining, TimeUnit.m)

            // Extract seconds
            amountOfSeconds = Utils.substractTimeUnit(amountOfMinutes.remaining, TimeUnit.s)

            if (amountOfSeconds.remaining) {
                throw new Error(
                    `${Utils.prefix}Incorrect data received for conversion: leftovers after conversion: ${amountOfSeconds.remaining}`
                )
            }
            let totalSeconds = 0
            totalSeconds += amountOfDays.amount * Utils.workingDayInSeconds
            totalSeconds += amountOfHours.amount * Utils.hourInSeconds
            totalSeconds += amountOfMinutes.amount * Utils.minuteInSeconds
            totalSeconds += amountOfSeconds.amount

            return totalSeconds
        }

        return 0
    }

    static getAllTagsFromText(text: string, processHashtagTags: boolean): Set<string> {
        let tags = new Set<string>([UiConstants.NoTag])

        const tryRemoveTagComment = (tagValue: string): string => {
            const colonIdx = tagValue.indexOf(':')
            if (colonIdx > 0) {
                // remove the comment from the tag
                return tagValue.slice(0, colonIdx)
            }

            return tagValue
        }
        const tryAddMainTag = (tagValue: string): void => {
            const slashIdx = tagValue.indexOf('/')

            if (slashIdx > 1) {
                // add the main-tag portion explicitely as a tag also
                tags = tags.add(tagValue.slice(0, slashIdx).trim())
            }
        }

        // TAG format = "[[tag characters]]" or "[[tag characters: additional comment]] or "#tag characters"
        // TAG & subTAG format = "[[main tag / sub tag]]" or "[[main tag / sub tag: additional comment]]"
        // Backslash is also accepted and changed into forward slash
        // Accidently, the delimiters can have become "\[\[" and "]]"
        if (text) {
            text.replace('\\[\\[', '[[')

            const bracketsStartIndex = text.indexOf('[[') + 2
            if (bracketsStartIndex >= 2) {
                const bracketsEndIndex = text.indexOf(']]', bracketsStartIndex)
                if (bracketsEndIndex > bracketsStartIndex) {
                    let bracketTagValue = tryRemoveTagComment(text.slice(bracketsStartIndex, bracketsEndIndex))
                    bracketTagValue = bracketTagValue.trim().toUpperCase().replace('\\', '/')

                    // remove the tag that was found from the text and search for more tags
                    const textWithoutTag = text.slice(0, bracketsStartIndex - 2) + text.slice(bracketsEndIndex + 2)
                    const recursiveTags = Utils.getAllTagsFromText(textWithoutTag, processHashtagTags)
                    tags = recursiveTags.add(bracketTagValue)

                    tryAddMainTag(bracketTagValue)
                }
            }

            const hashtagStartIndex = text.indexOf('#') + 1
            if (hashtagStartIndex >= 1 && processHashtagTags) {
                let hashtagEndIndex = Utils.findTagEndPosition(text, hashtagStartIndex)
                if (hashtagEndIndex < 0) {
                    hashtagEndIndex = text.length
                }

                if (hashtagEndIndex > hashtagStartIndex) {
                    let hashtagTagValue = tryRemoveTagComment(text.slice(hashtagStartIndex, hashtagEndIndex))
                    if (hashtagTagValue.length > 2) {
                        hashtagTagValue = hashtagTagValue.trim().toUpperCase().replace('\\', '/')

                        // remove the tag that was found from the text and search for more tags
                        const textWithoutTag = text.slice(0, hashtagStartIndex - 1) + text.slice(hashtagEndIndex)
                        const recursiveTags = Utils.getAllTagsFromText(textWithoutTag, processHashtagTags)
                        tags = recursiveTags.add(hashtagTagValue)

                        tryAddMainTag(hashtagTagValue)
                    }
                }
            }
        }

        if (tags.size > 1) {
            // 1 or more real tags were found: remove the NoTag
            tags.delete(UiConstants.NoTag)
        }

        return tags
    }

    static removeAllTagsFromText(text: string, processHashtagTags: boolean): string {
        // Filter out [[tags]] = any text between "[[" and "]]"
        // Accidently, the delimiters can have become "\[\[" and "]]"
        let result = text
        if (result) {
            result.replace('\\[\\[', '[[')
            const bracketsStartIndex = text.indexOf('[[')
            if (bracketsStartIndex >= 0) {
                const bracketsEndIndex = text.indexOf(']]', bracketsStartIndex)
                if (bracketsEndIndex > 0) {
                    result = result.slice(0, bracketsStartIndex) + result.slice(bracketsEndIndex + 2)
                    result = Utils.removeAllTagsFromText(result.trim(), processHashtagTags) // recursive call: text may contain multiple tags
                }
            }

            // Filter out #tags = any text beginning with "#""
            const hashtagStartIndex = text.indexOf('#')
            if (hashtagStartIndex >= 1 && processHashtagTags) {
                let hashtagEndIndex = Utils.findTagEndPosition(text, hashtagStartIndex + 1)
                if (hashtagEndIndex < 0) {
                    hashtagEndIndex = text.length
                }
                if (hashtagEndIndex > 0) {
                    result = result.slice(0, hashtagStartIndex) + result.slice(hashtagEndIndex + 1)
                    result = Utils.removeAllTagsFromText(result.trim(), processHashtagTags) // recursive call: text may contain multiple tags
                }
            }
        }

        return result
    }

    private static findTagEndPosition(text: string, start: number): number {
        let i = start
        let end = -1
        while (i < text.length) {
            const charCode = text.charCodeAt(i)
            if (
                !(
                    (charCode >= 48 && charCode <= 57) ||
                    (charCode >= 65 && charCode <= 90) ||
                    (charCode >= 97 && charCode <= 122) ||
                    charCode === 47 ||
                    charCode === 45 ||
                    charCode === 95
                )
            ) {
                // (charCode >= 48 && charCode <= 57) represents a digit between 0 and 9
                // (charCode >= 65 && charCode <= 90) represent a character from A to Z
                // (charCode >= 97 && charCode <= 122) represents a character from a to z
                // (charCode == 47) represents a /
                // (charCode == 45) represents a -
                // (charCode == 95) represents a _
                // the character is not a text or numeric character (excluding "/", "-" or "_"), so this is the end position
                end = i
                break
            }
            i++
        }

        return end
    }

    private static substractTimeUnit(timeString: string, unit: TimeUnit): TimeUnitExtractResult {
        const result: TimeUnitExtractResult = { timeUnit: unit, amount: 0, remaining: timeString }

        if (timeString) {
            let tempTimeString = timeString.toLowerCase()
            const unitAsString = TimeUnit[unit]

            const idx = tempTimeString.indexOf(unitAsString)

            if (idx > -1) {
                // Remove the time unit marker from the string
                tempTimeString = tempTimeString.slice(0, idx) + tempTimeString.slice(idx + 1)

                let amountAsString = ''
                for (let i = idx - 1; i >= 0; i--) {
                    let letterAsString = tempTimeString[i]
                    if (
                        Number(letterAsString) ||
                        letterAsString === '0' ||
                        letterAsString === ',' ||
                        letterAsString === '.'
                    ) {
                        // If the letter is a number, a comma or a dot,
                        // we remove it from the timeString, and add it to the amountAsString
                        tempTimeString = tempTimeString.slice(0, i) + tempTimeString.slice(i + 1)
                        letterAsString = letterAsString.replace(',', '.')
                        amountAsString = letterAsString + amountAsString
                    } else {
                        // If the letter is not a number, we stop the loop.
                        break
                    }
                }

                if (amountAsString) {
                    const amountAsNumber = Number(amountAsString)
                    if (amountAsNumber) {
                        result.amount = amountAsNumber
                        result.remaining = tempTimeString
                    } else {
                        throw new Error(
                            `${Utils.prefix}Incorrect data received for conversion: ${amountAsString} cannot be converted into ${unitAsString}`
                        )
                    }
                } else {
                    result.remaining = tempTimeString
                }
            }
        }

        return result
    }
}

export interface TimeUnitExtractResult {
    timeUnit: TimeUnit
    amount: number
    remaining: string
}

export enum TimeUnit {
    s, // second
    m, // minute
    h, // hour
    d // day
}
